import type { CheckoutSessionOwnershipToken } from "@circle-react/components/Paywalls/Checkout/sessionOwnership";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface CheckoutSessionOwnershipContextValue {
  getTokenForSession: (
    sessionId: number | null | undefined,
  ) => CheckoutSessionOwnershipToken | null;
  storeTokenForSession: (
    sessionId: number,
    token: CheckoutSessionOwnershipToken,
  ) => void;
}

interface CheckoutSessionOwnershipProviderProps {
  children: ReactNode;
}

const CheckoutSessionOwnershipContext =
  createContext<CheckoutSessionOwnershipContextValue | null>(null);
CheckoutSessionOwnershipContext.displayName = "CheckoutSessionOwnershipContext";

export const CheckoutSessionOwnershipProvider = ({
  children,
}: CheckoutSessionOwnershipProviderProps) => {
  const [tokensBySessionId, setTokensBySessionId] = useState<
    ReadonlyMap<number, CheckoutSessionOwnershipToken>
  >(() => new Map());

  const storeTokenForSession = useCallback(
    (sessionId: number, token: CheckoutSessionOwnershipToken) => {
      setTokensBySessionId(currentTokens => {
        if (currentTokens.get(sessionId) === token) return currentTokens;

        const nextTokens = new Map(currentTokens);
        nextTokens.set(sessionId, token);
        return nextTokens;
      });
    },
    [],
  );

  const getTokenForSession = useCallback(
    (sessionId: number | null | undefined) =>
      sessionId == null ? null : (tokensBySessionId.get(sessionId) ?? null),
    [tokensBySessionId],
  );

  const value = useMemo(
    () => ({ getTokenForSession, storeTokenForSession }),
    [getTokenForSession, storeTokenForSession],
  );

  return (
    <CheckoutSessionOwnershipContext.Provider value={value}>
      {children}
    </CheckoutSessionOwnershipContext.Provider>
  );
};

export const useCheckoutSessionOwnership =
  (): CheckoutSessionOwnershipContextValue => {
    const context = useContext(CheckoutSessionOwnershipContext);

    if (!context) {
      throw new Error(
        "useCheckoutSessionOwnership must be used within CheckoutSessionOwnershipProvider",
      );
    }

    return context;
  };
